import type { Circuit, PlayerTrackPosition } from '../types/track.js';
import { isStartSideOnSfOverlap } from './circuitLoop.js';
import { standingCompletedLapsOnCircuit } from './lapDisplay.js';
import { computeTrackProgressScore } from './trackProgress.js';
import { type SameCellOvertakePair, type TurnOrderPlayerProgress } from './turnOrder.js';

/** Entrées pour trier le classement (ordre de passage ligne d'arrivée pour les terminés). */
export interface RaceRankingEntry {
  playerIndex: number;
  finished: boolean;
  /** 0 = pas encore classé à l'arrivée ; sinon ordre de passage (1 = premier). */
  finishStamp: number;
  lapCount: number;
  /** Index sur le circuit principal (affichage / replays). */
  pathIndex: number;
  /** Progression réelle (joker, ligne de départ). Prioritaire pour le tri en course. */
  progressScore?: number;
  /** Tampon d’arrivée sur la case courante — tie-break aligné sur l’ordre de jeu. */
  arrivalStamp?: number;
  totalDiceRolls: number;
  jokerBranchIndex?: number | null;
  startLaneIndex?: number | null;
  skipStartLineCrossingsRemaining?: number;
  hadStartLineSkipAtStart?: boolean;
}

export function raceProgressForSort(entry: RaceRankingEntry): number {
  return entry.progressScore ?? entry.pathIndex;
}

/** Vrai si tous les pilotes encore en course ont le même nombre de lancers (manche complète). */
export function isUniformDiceRollCountAmongActive(entries: readonly RaceRankingEntry[]): boolean {
  const rolls = entries.filter((e) => !e.finished).map((e) => e.totalDiceRolls);
  if (rolls.length <= 1) return true;
  return Math.min(...rolls) === Math.max(...rolls);
}

export function buildRaceRankingEntry(
  circuit: Circuit | null | undefined,
  playerIndex: number,
  data: {
    finished: boolean;
    finishStamp: number;
    lapCount: number;
    mainPathIndex: number;
    jokerBranchIndex?: number | null;
    startLaneIndex?: number | null;
    arrivalStamp?: number;
    totalDiceRolls: number;
    startSideOnSfOverlap?: boolean;
    skipStartLineCrossingsRemaining?: number;
    hadStartLineSkipAtStart?: boolean;
    uniformDiceRollsAmongField?: boolean;
  }
): RaceRankingEntry {
  const position: PlayerTrackPosition = {
    mainPathIndex: data.mainPathIndex,
    jokerBranchIndex: data.jokerBranchIndex ?? null,
    startLaneIndex: data.startLaneIndex ?? null,
  };
  return {
    playerIndex,
    finished: data.finished,
    finishStamp: data.finishStamp,
    lapCount: data.lapCount,
    pathIndex: data.mainPathIndex,
    progressScore: circuit
      ? computeTrackProgressScore(circuit, position, data.lapCount, {
          totalDiceRolls: data.totalDiceRolls,
          startSideOnSfOverlap: data.startSideOnSfOverlap,
          skipStartLineCrossingsRemaining: data.skipStartLineCrossingsRemaining,
          hadStartLineSkipAtStart: data.hadStartLineSkipAtStart,
          uniformDiceRollsAmongField: data.uniformDiceRollsAmongField,
        })
      : undefined,
    arrivalStamp: data.arrivalStamp,
    totalDiceRolls: data.totalDiceRolls,
    jokerBranchIndex: data.jokerBranchIndex ?? null,
    startLaneIndex: data.startLaneIndex ?? null,
    skipStartLineCrossingsRemaining: data.skipStartLineCrossingsRemaining,
    hadStartLineSkipAtStart: data.hadStartLineSkipAtStart,
  };
}

export function buildRaceRankingEntries(
  circuit: Circuit | null | undefined,
  count: number,
  getData: (playerIndex: number) => {
    finished: boolean;
    finishStamp: number;
    lapCount: number;
    mainPathIndex: number;
    jokerBranchIndex?: number | null;
    startLaneIndex?: number | null;
    arrivalStamp?: number;
    totalDiceRolls: number;
    skipStartLineCrossingsRemaining?: number;
    hadStartLineSkipAtStart?: boolean;
  }
): RaceRankingEntry[] {
  const indices = Array.from({ length: count }, (_, i) => i);
  const allData = indices.map((playerIndex) => getData(playerIndex));
  const activeRolls = allData.filter((d) => !d.finished).map((d) => d.totalDiceRolls);
  const uniformDiceRollsAmongField =
    activeRolls.length <= 1 ||
    (activeRolls.length > 0 &&
      Math.min(...activeRolls) === Math.max(...activeRolls) &&
      activeRolls[0] > 0);

  return indices.map((playerIndex) => {
    const data = allData[playerIndex];
    const position: PlayerTrackPosition = {
      mainPathIndex: data.mainPathIndex,
      jokerBranchIndex: data.jokerBranchIndex ?? null,
      startLaneIndex: data.startLaneIndex ?? null,
    };
    const startSideOnSfOverlap =
      circuit != null
        ? isStartSideOnSfOverlap(
            circuit,
            playerIndex,
            position,
            data.lapCount,
            (i) => ({
              mainPathIndex: getData(i).mainPathIndex,
              jokerBranchIndex: getData(i).jokerBranchIndex ?? null,
              startLaneIndex: getData(i).startLaneIndex ?? null,
            }),
            (i) => getData(i).lapCount,
            indices
          )
        : false;
    return buildRaceRankingEntry(circuit, playerIndex, {
      ...data,
      startSideOnSfOverlap,
      uniformDiceRollsAmongField,
    });
  });
}

/**
 * Trie les indices joueurs : terminés par ordre de passage à la ligne d'arrivée,
 * puis en course par progression (tours, position sur le tracé).
 */
/** Tours affichés puis index piste (demi / finale Hill Valley). */
function standingOnTrack(entry: RaceRankingEntry, circuit: Circuit | null | undefined): number {
  if (!circuit) return entry.lapCount;
  const position: PlayerTrackPosition = {
    mainPathIndex: entry.pathIndex,
    jokerBranchIndex: entry.jokerBranchIndex ?? null,
    startLaneIndex: entry.startLaneIndex ?? null,
  };
  return standingCompletedLapsOnCircuit(circuit, position, entry.lapCount, {
    totalDiceRolls: entry.totalDiceRolls,
    skipStartLineCrossingsRemaining: entry.skipStartLineCrossingsRemaining,
    hadStartLineSkipAtStart: entry.hadStartLineSkipAtStart,
  });
}

/** idx 0 après wrap : +1 tour affiché (lapCount+1) alors que le peloton est plus loin sur le même tour. */
function isIdxZeroWrapStandingInflation(entry: RaceRankingEntry, standing: number): boolean {
  return (
    entry.pathIndex === 0 &&
    entry.jokerBranchIndex == null &&
    entry.startLaneIndex == null &&
    entry.lapCount > 0 &&
    standing > entry.lapCount
  );
}

/** idx 0 crédité +1 tour affiché alors qu'un rival est plus loin sur le même lapCount. */
function preferPathIndexOverIdxZeroStanding(
  ea: RaceRankingEntry,
  eb: RaceRankingEntry,
  standA: number,
  standB: number
): boolean {
  if (ea.lapCount !== eb.lapCount || standA === standB) return false;
  const inflateA = isIdxZeroWrapStandingInflation(ea, standA);
  const inflateB = isIdxZeroWrapStandingInflation(eb, standB);
  if (inflateA && !inflateB && eb.pathIndex > ea.pathIndex) return true;
  if (inflateB && !inflateA && ea.pathIndex > eb.pathIndex) return true;
  return false;
}

export function sortPlayerIndicesForRanking(
  entries: RaceRankingEntry[],
  circuit?: Circuit | null
): number[] {
  const byIndex = new Map(entries.map((e) => [e.playerIndex, e]));
  const indices = entries.map((e) => e.playerIndex);
  const diceTiebreakActive = isUniformDiceRollCountAmongActive(entries);
  indices.sort((a, b) => {
    const ea = byIndex.get(a)!;
    const eb = byIndex.get(b)!;
    if (ea.finished && eb.finished) {
      const sa = ea.finishStamp > 0 ? ea.finishStamp : Number.MAX_SAFE_INTEGER;
      const sb = eb.finishStamp > 0 ? eb.finishStamp : Number.MAX_SAFE_INTEGER;
      if (sa !== sb) return sa - sb;
      return a - b;
    }
    if (ea.finished && !eb.finished) return -1;
    if (!ea.finished && eb.finished) return 1;
    const progA = raceProgressForSort(ea);
    const progB = raceProgressForSort(eb);
    // progressScore = source de vérité (skip S/F, joker, wrap) — aligné sur la piste.
    if (ea.progressScore != null && eb.progressScore != null) {
      if (progB !== progA) return progB - progA;
    } else if (!ea.finished && !eb.finished && circuit) {
      const semiA = ea.hadStartLineSkipAtStart === true;
      const semiB = eb.hadStartLineSkipAtStart === true;
      const onMainA = ea.startLaneIndex == null && ea.jokerBranchIndex == null;
      const onMainB = eb.startLaneIndex == null && eb.jokerBranchIndex == null;
      // Repli sans progressScore : tours affichés + index (demi / finale).
      if ((semiA || semiB) && onMainA && onMainB) {
        const standA = standingOnTrack(ea, circuit);
        const standB = standingOnTrack(eb, circuit);
        if (standB !== standA) {
          if (preferPathIndexOverIdxZeroStanding(ea, eb, standA, standB)) {
            if (eb.pathIndex !== ea.pathIndex) return eb.pathIndex - ea.pathIndex;
          } else {
            return standB - standA;
          }
        }
        if (eb.pathIndex !== ea.pathIndex) return eb.pathIndex - ea.pathIndex;
      }
      if (eb.lapCount !== ea.lapCount) return eb.lapCount - ea.lapCount;
      if (progB !== progA) return progB - progA;
    } else {
      if (eb.lapCount !== ea.lapCount) return eb.lapCount - ea.lapCount;
      if (progB !== progA) return progB - progA;
    }
    if (diceTiebreakActive && ea.totalDiceRolls !== eb.totalDiceRolls) {
      return ea.totalDiceRolls - eb.totalDiceRolls;
    }
    const stampA = ea.arrivalStamp ?? 0;
    const stampB = eb.arrivalStamp ?? 0;
    if (stampA !== stampB) return stampA - stampB;
    return a - b;
  });
  return indices;
}

export interface BuildRaceRankingOptions {
  circuit?: Circuit | null;
  sameCellOvertakes?: readonly SameCellOvertakePair[];
  getTurnProgress?: (playerIndex: number) => TurnOrderPlayerProgress;
}

export function buildRaceRanking(
  entries: RaceRankingEntry[],
  options?: BuildRaceRankingOptions
): { playerIndex: number; rank: number }[] {
  const sorted = sortPlayerIndicesForRanking(entries, options?.circuit);
  return sorted.map((playerIndex, i) => ({ playerIndex, rank: i + 1 }));
}

/**
 * Ordre de lancer en début de tour : actifs du 1er au dernier au classement, puis terminés.
 * Même ordre relatif que `buildRaceRanking` parmi les actifs.
 */
export function turnOrderFromRanking(
  ranking: readonly { playerIndex: number; rank: number }[],
  isFinished: (playerIndex: number) => boolean
): number[] {
  const byRank = [...ranking].sort((a, b) => a.rank - b.rank);
  const active = byRank.filter((r) => !isFinished(r.playerIndex)).map((r) => r.playerIndex);
  const inactive = byRank.filter((r) => isFinished(r.playerIndex)).map((r) => r.playerIndex);
  return [...active, ...inactive];
}

/** Construit les entrées de classement à partir de tableaux par slot (tests / outils). */
export function raceRankingEntriesFromSlices(
  count: number,
  slices: {
    finished?: boolean[];
    finishStamp?: number[];
    lapCount?: number[];
    pathIndex?: number[];
    totalDiceRolls?: number[];
  } = {}
): RaceRankingEntry[] {
  return Array.from({ length: count }, (_, playerIndex) => ({
    playerIndex,
    finished: slices.finished?.[playerIndex] ?? false,
    finishStamp: slices.finishStamp?.[playerIndex] ?? 0,
    lapCount: slices.lapCount?.[playerIndex] ?? 0,
    pathIndex: slices.pathIndex?.[playerIndex] ?? 0,
    progressScore: undefined,
    totalDiceRolls: slices.totalDiceRolls?.[playerIndex] ?? 0,
  }));
}

/**
 * Régression : ancien tri par pathIndex parmi les terminés — le plus loin gagnait à tort.
 * Retourne true si le classement respecte l’ordre de finishStamp (et non pathIndex).
 */
export function finishedRankingUsesFinishStampNotPath(entries: RaceRankingEntry[]): boolean {
  const finished = entries.filter((e) => e.finished && e.finishStamp > 0);
  if (finished.length < 2) return true;
  const byFinish = [...finished].sort((a, b) => a.finishStamp - b.finishStamp);
  const byPath = [...finished].sort((a, b) => b.pathIndex - a.pathIndex);
  const sameOrder = byFinish.every((e, i) => byPath[i]?.playerIndex === e.playerIndex);
  const pathWouldWin = byPath[0]?.playerIndex !== byFinish[0]?.playerIndex;
  return sameOrder || !pathWouldWin;
}
